import { useState } from "react";

import { Group, Table } from "@mantine/core";

import { IconFlag } from "@tabler/icons-react";

import EmptyRow from "@/components/EmptyRow";
import ScrollableTable from "@/components/ScrollableTable";
import SortableTh from "@/components/SortableTh";
import { TextWrapper } from "@/components/TextWrapper";
import { shortUSDateFormat } from "@/utils/datetime";

import { NO_ARCHIVED_CLIENT_FLAGS_MESSAGE } from "../constants/client-flags";
import { FLAG_CATEGORY_CODINGS } from "../constants/flags";
import { ARCHIVE_COLUMN_WIDTHS, ARCHIVE_TABLE_MIN_WIDTH, EMPTY_CELL } from "../constants/ui";
import { ArchivedClientFlag } from "../interfaces/client-flags";
import { ArchiveSortKey, sortArchivedClientFlags } from "../utils/flag-archive";

import FlagDeprecatedBadge from "./FlagDeprecatedBadge";
import FlagPriorityBadge from "./FlagPriorityBadge";
import FlagPriorityDot from "./FlagPriorityDot";

const COLUMNS: { key: ArchiveSortKey; label: string; width: string }[] = [
  { key: ArchiveSortKey.Name, label: "Name", width: ARCHIVE_COLUMN_WIDTHS.name },
  { key: ArchiveSortKey.Priority, label: "Priority", width: ARCHIVE_COLUMN_WIDTHS.priority },
  { key: ArchiveSortKey.Category, label: "Category", width: ARCHIVE_COLUMN_WIDTHS.category },
  { key: ArchiveSortKey.Date, label: "Date", width: ARCHIVE_COLUMN_WIDTHS.date },
  { key: ArchiveSortKey.Provider, label: "Provider", width: ARCHIVE_COLUMN_WIDTHS.provider },
];

/**
 * The flags a chart no longer carries, one row per removal.
 *
 * Read-only throughout: no edit, no delete, and no restore. A flag comes back by being applied
 * again from the drawer, which writes a new record and leaves this one as history.
 *
 * The sorted column lives in component state, not the URL. It is a quick look within one chart, not
 * a view worth linking to.
 */
export default function FlagArchiveTable({ flags }: { flags: ArchivedClientFlag[] }) {
  const [sortParams, setSortParams] = useState(() => new URLSearchParams());

  const handleSort = (sortKey: string | null) => {
    const nextParams = new URLSearchParams();
    if (sortKey) nextParams.set("sort", sortKey);

    setSortParams(nextParams);
  };

  const sortedFlags = sortArchivedClientFlags(flags, sortParams.get("sort"));

  return (
    <ScrollableTable layout="fixed" miw={ARCHIVE_TABLE_MIN_WIDTH} data-testid="flag-archive-table">
      <Table.Thead bg="cream.3">
        <Table.Tr>
          {COLUMNS.map(({ key, label, width }) => (
            <SortableTh
              key={key}
              w={width}
              sortKey={key}
              onSort={handleSort}
              searchParams={sortParams}
            >
              {label}
            </SortableTh>
          ))}
          {/* Free text has no meaningful order, so Reason is the one column that does not sort. */}
          <Table.Th w={ARCHIVE_COLUMN_WIDTHS.reason}>Reason</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sortedFlags.map((flag) => (
          <Table.Tr key={flag.id} data-testid={`flag-archive-row-${flag.id}`}>
            <Table.Td>
              <Group gap="xs" wrap="nowrap">
                <FlagPriorityDot priority={flag.priority} isDeprecated={flag.isDeprecated} />
                <TextWrapper>{flag.name}</TextWrapper>
              </Group>
            </Table.Td>
            <Table.Td>
              {flag.isDeprecated && <FlagDeprecatedBadge />}
              {!flag.isDeprecated && flag.priority && (
                <FlagPriorityBadge withDot={false} priority={flag.priority} />
              )}
              {!flag.isDeprecated && !flag.priority && (
                <TextWrapper c="gray.7">{EMPTY_CELL}</TextWrapper>
              )}
            </Table.Td>
            <Table.Td>
              <TextWrapper c="gray.7">
                {flag.category ? FLAG_CATEGORY_CODINGS[flag.category].display : EMPTY_CELL}
              </TextWrapper>
            </Table.Td>
            <Table.Td>
              <TextWrapper c="gray.7">
                {flag.removedAt ? shortUSDateFormat(flag.removedAt) : EMPTY_CELL}
              </TextWrapper>
            </Table.Td>
            <Table.Td>
              <TextWrapper c="gray.7">{flag.removedBy || EMPTY_CELL}</TextWrapper>
            </Table.Td>
            <Table.Td>
              <TextWrapper c="gray.7">{flag.reason || EMPTY_CELL}</TextWrapper>
            </Table.Td>
          </Table.Tr>
        ))}

        {!sortedFlags.length && (
          <EmptyRow Icon={IconFlag} text={NO_ARCHIVED_CLIENT_FLAGS_MESSAGE} />
        )}
      </Table.Tbody>
    </ScrollableTable>
  );
}
